import { useAreaIds } from "@/pages/workOrders/hooks/useAreaIds";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	type CalendarEvent,
	upsertCalendarEvent,
} from "@/shared/repositories/CalendarEventRepository";
import {
	upsertLineScheduleDay,
	type ShiftSpellChange,
} from "@/shared/repositories/LineScheduleRepository";
import {
	type CalendarWorkOrder,
	getWorkOrders,
	moveWorkOrder,
} from "@/shared/repositories/WorkOrderRepository";
import {
	getLastPlanChange,
	type PlanChange,
	type PlanChangeRef,
	redoPlanChange,
	toPlanChangeRefs,
	undoPlanChange,
} from "@/shared/repositories/PlanChangeRepository";
import { useSessionPlanChanges } from "./hooks/useSessionPlanChanges";
import { ATHENS_GRAY } from "@/shared/colors";
import { invalidatePlanQueries } from "@/shared/utils/planCache";
import {
	ErrorModal,
	getMomentTz,
	HelmetDexteel,
	TimeService,
} from "@dexteel/mesf-core";
import {
	Backdrop,
	Box,
	CircularProgress,
	Grid2,
	LinearProgress,
	Paper,
	Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalendarLegend } from "./components/CalendarLegend";
import { CalendarToolbar } from "./components/CalendarToolbar";
import { UndoRedoButtons } from "./components/UndoRedoButtons";
import { ConfirmMoveModal } from "./components/ConfirmMoveModal";
import { BreakPlanConfirmationModal } from "./components/BreakPlanConfirmationModal";
import { DayConfigModal } from "./components/DayConfigModal";
import { MonthGrid } from "./components/MonthGrid";
import { MonthGridSkeleton } from "./components/MonthGridSkeleton";
import { EventEditorModal } from "./components/EventEditorModal";
import { useCalendarData } from "./hooks/useCalendarData";
import { useCalendarAssetNavigation } from "./hooks/useCalendarAssetNavigation";
import { type MoveRequest, useCalendarDrag } from "./hooks/useCalendarDrag";
import { useCalendarMenus } from "./hooks/useCalendarMenus";
import { useDeleteCalendarItem } from "./hooks/useDeleteCalendarItem";
import {
	type DayKind,
	dayKindOf,
	shiftsPerDayOf,
	suggestFreeWindow,
	toDateKey,
} from "./utils/calendarUtils";

const moment = getMomentTz();

const SOURCE_LABELS: Record<string, string> = {
	DayConfig: "Day Config",
	WorkOrderDelete: "Delete work order",
	EventDelete: "Delete event",
};

const changeLabel = (c: PlanChange) =>
	`${SOURCE_LABELS[c.Source] ?? c.Source} (${c.MovedCount} affected)`;

type EditorState =
	| { mode: "create"; start: Date; end: Date }
	| { mode: "event"; event: CalendarEvent }
	| { mode: "wo"; wo: CalendarWorkOrder }
	| null;

export const CalendarPage = () => {
	const { areaIds } = useAreaIds();
	const { assetId, assetName, handleAssetChange } =
		useCalendarAssetNavigation(areaIds);

	const [currentDate, setCurrentDate] = useState(() => new Date());
	const [dayConfig, setDayConfig] = useState<Date | null>(null);
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [modal, setModal] = useState<EditorState>(null);
	const [moveRequest, setMoveRequest] = useState<MoveRequest | null>(null);
	const suppressDayClickRef = useRef(false);
	const suppressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const queryClient = useQueryClient();
	const serverTz = TimeService.getInstance().getServerTimeZone();

	const {
		calData,
		isCalendarLoading,
		isCalendarFetching,
		isCalendarStale,
		workOrdersList,
		calendarItems,
		shiftsByDay,
		lineDefaultKind,
		scheduleByDay,
		shiftBandsByDay,
	} = useCalendarData(assetId, currentDate, serverTz, setError);

	// Maintenance can also move work orders on other lines.
	const invalidateEvents = useCallback(
		(refs: PlanChangeRef[] = []) =>
			invalidatePlanQueries(queryClient, {
				assetIds: [assetId, ...refs.map((r) => r.assetId)],
				workOrders: true,
				planChange: true,
			}),
		[queryClient, assetId],
	);

	const { data: lastPlanChange } = useQuery({
		queryKey: ["lastPlanChange", assetId],
		queryFn: ({ signal }) => getLastPlanChange(assetId as number, signal),
		enabled: !!assetId,
		staleTime: 30_000,
		onError: (e: Error) => setError(e.message),
	});

	const {
		changes: sessionChanges,
		undone: sessionUndone,
		recordChanges,
		markUndone,
		markRedone,
	} = useSessionPlanChanges(assetId);

	const undoMutation = useMutation({
		mutationFn: () => undoPlanChange(assetId as number),
		onSuccess: () => {
			const undone = lastPlanChange?.undo?.PlanChangeId;
			if (undone != null) markUndone(undone);
			invalidateEvents();
		},
		onError: (e: Error) => setError(e.message),
	});

	const redoMutation = useMutation({
		mutationFn: () => redoPlanChange(assetId as number),
		onSuccess: () => {
			const redone = lastPlanChange?.redo?.PlanChangeId;
			if (redone != null) markRedone(redone);
			invalidateEvents();
		},
		onError: (e: Error) => setError(e.message),
	});

	const dayConfigMutation = useMutation({
		mutationFn: async ({
			kind,
			spells,
		}: {
			kind: DayKind | null;
			spells: ShiftSpellChange[];
		}) => {
			if (!assetId || !dayConfig) return null;
			return upsertLineScheduleDay({
				assetId,
				productionDate: toDateKey(dayConfig),
				shiftsPerDay: kind ? shiftsPerDayOf(kind) : null,
				applyScheme: kind != null,
				shiftSpells: spells,
			});
		},
		onSuccess: (row) => {
			// A shape change is one undo batch, filed like a move
			if (row) recordChanges(toPlanChangeRefs([row]));
			setDayConfig(null);
		},
		onError: (e: Error) => setError(e.message),
		onSettled: () =>
			invalidatePlanQueries(queryClient, {
				assetIds: [assetId],
				breaks: true,
				workOrders: true,
				planChange: true,
			}),
	});

	const openNewEvent = (date: Date) => {
		const key = toDateKey(date);
		const shifts = shiftsByDay.get(key) ?? [];
		const dayStart = moment.tz(key, "YYYY-MM-DD", serverTz);
		const dayEnd = dayStart.clone().add(1, "day");
		const preferredStart = shifts[0]
			? moment.utc(shifts[0].Start).valueOf()
			: dayStart.clone().hour(7).valueOf();
		const preferredMs = shifts[0]
			? moment.utc(shifts[0].End).diff(moment.utc(shifts[0].Start))
			: 8 * 3_600_000;

		const { start, end } = suggestFreeWindow({
			preferredStart,
			preferredMs,
			lowerBound: dayStart.valueOf(),
			busy: (calData?.events ?? [])
				.map((ev) => ({
					s: moment.utc(ev.Start).valueOf(),
					e: moment.utc(ev.End).valueOf(),
				}))
				.filter((w) => w.s < dayEnd.valueOf() && w.e > dayStart.valueOf()),
		});
		setModal({ mode: "create", start, end });
	};

	const selectedWo = modal?.mode === "wo" ? modal.wo : null;

	// The chip carries only calendar fields; the WO modal needs the full row.
	// Shares its cache key with WorkOrdersPage.
	const { data: shiftWorkOrders, isFetching: isWorkOrderLoading } = useQuery({
		queryKey: ["workOrders", assetId, selectedWo?.DisplayShiftId],
		queryFn: () =>
			getWorkOrders(assetId as number, selectedWo?.DisplayShiftId as number),
		enabled: !!assetId && !!selectedWo,
		staleTime: 30_000,
		meta: { realtime: ["FAB.GetWorkOrders"] },
		onError: (e: Error) => {
			setModal(null);
			setError(e.message);
		},
	});

	const editWorkOrder = useMemo(
		() =>
			selectedWo
				? (shiftWorkOrders?.find(
						(r) => r.WorkOrderId === selectedWo.WorkOrderId,
					) ?? null)
				: null,
		[shiftWorkOrders, selectedWo],
	);

	const editorProps = useMemo(() => {
		if (!modal || (modal.mode === "wo" && (!editWorkOrder || !assetId)))
			return null;
		return {
			defaultStart: modal.mode === "create" ? modal.start : null,
			defaultEnd: modal.mode === "create" ? modal.end : null,
			event:
				modal.mode === "event"
					? modal.event
					: modal.mode === "wo"
						? (calData?.events.find(
								(ev) => ev.WorkOrderId === editWorkOrder?.WorkOrderId,
							) ?? null)
						: null,
			workOrder: modal.mode === "wo" ? editWorkOrder : null,
		};
	}, [modal, editWorkOrder, assetId, calData]);

	const handleMoveSaved = (refs: PlanChangeRef[]) => {
		recordChanges(refs);
		invalidateEvents(refs);
		setMoveRequest(null);
	};

	const moveMutation = useMutation({
		mutationFn: async (req: MoveRequest) => {
			if (req.item.kind === "wo") {
				return moveWorkOrder({
					workOrderId: req.item.wo.WorkOrderId,
					targetShiftId: req.targetShift?.ShiftId ?? null,
					insertAfterWorkOrderId: req.insertAfterWorkOrderId,
					eventStart: req.newStart.toISOString(),
					eventEnd: req.newEnd.toISOString(),
				});
			}
			const result = await upsertCalendarEvent({
				eventId: req.item.event.EventId,
				eventTypeCode: req.item.event.EventTypeCode,
				assetIds:
					req.item.event.AssetId != null ? [req.item.event.AssetId] : null,
				start: req.newStart.toISOString(),
				end: req.newEnd.toISOString(),
				comments: req.item.event.Comments,
			});
			return result.planChanges;
		},
		onSuccess: handleMoveSaved,
		onError: (e: Error) => {
			setMoveRequest(null);
			setError(e.message);
		},
	});

	// Keep undo disabled until the refreshed plan arrives.
	const undoBusy =
		undoMutation.isLoading || redoMutation.isLoading || isCalendarFetching;

	const undoCandidate = lastPlanChange?.undo ?? null;
	const redoCandidate = lastPlanChange?.redo ?? null;
	const canUndo =
		!!undoCandidate && sessionChanges.includes(undoCandidate.PlanChangeId);
	const canRedo =
		!!redoCandidate && sessionUndone.includes(redoCandidate.PlanChangeId);
	const undoTitle =
		canUndo && undoCandidate
			? `Undo ${changeLabel(undoCandidate)}`
			: undoCandidate
				? "Last change was made outside this session"
				: "Nothing to undo";
	const redoTitle =
		canRedo && redoCandidate
			? `Redo ${changeLabel(redoCandidate)}`
			: "Nothing to redo";

	const { drag, dropLine, preview, freeMode, ghostRef, handleItemMouseDown } =
		useCalendarDrag({
			items: calendarItems,
			workOrdersList,
			dayShifts: calData?.dayShifts ?? [],
			layoutKey: `${currentDate.getFullYear()}-${currentDate.getMonth()}`,
			stale: isCalendarStale,
			onOpenWorkOrder: (wo) => setModal({ mode: "wo", wo }),
			onOpenEvent: (event) => setModal({ mode: "event", event }),
			onMoveRequest: setMoveRequest,
			onDragEnd: () => {
				suppressDayClickRef.current = true;
				if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
				suppressTimerRef.current = setTimeout(() => {
					suppressDayClickRef.current = false;
				}, 100);
			},
		});

	useEffect(
		() => () => {
			if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
		},
		[],
	);

	const deleter = useDeleteCalendarItem(recordChanges, setError);

	const { menuTargetKey, onDayContextMenu, onItemContextMenu, menus } =
		useCalendarMenus({
			onNewEvent: openNewEvent,
			onDayConfig: setDayConfig,
			onEditItem: (item) =>
				setModal(
					item.kind === "wo"
						? { mode: "wo", wo: item.wo }
						: { mode: "event", event: item.event },
				),
			onDelete: deleter.requestDelete,
		});

	const goToMonth = (offset: number) =>
		setCurrentDate(
			(prev) => new Date(prev.getFullYear(), prev.getMonth() + offset, 1),
		);

	return (
		<>
			<HelmetDexteel title="Production Calendar" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				pb={2}
				style={{ backgroundColor: ATHENS_GRAY }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Production Calendar
					</Typography>
				</Grid2>

				<CalendarToolbar
					currentDate={currentDate}
					assetName={assetName ?? null}
					areaIds={areaIds}
					onMonthOffset={goToMonth}
					onToday={() => setCurrentDate(new Date())}
					onAssetChange={handleAssetChange}
				/>

				<Grid2 size={{ md: 12, xs: 12 }}>
					<Paper sx={{ p: 2, position: "relative" }}>
						<Box sx={{ position: "absolute", top: 0, left: 0, right: 0 }}>
							{isCalendarFetching && !isCalendarLoading && <LinearProgress />}
						</Box>

						{isCalendarLoading ? (
							<MonthGridSkeleton currentDate={currentDate} />
						) : (
							<MonthGrid
								currentDate={currentDate}
								scheduleByDay={scheduleByDay}
								shiftBandsByDay={shiftBandsByDay}
								items={calendarItems}
								drag={drag}
								dropLine={dropLine}
								preview={preview}
								freeMode={freeMode}
								stale={isCalendarStale}
								ghostRef={ghostRef}
								onMonthOffset={goToMonth}
								onDayClick={(date) => {
									if (suppressDayClickRef.current) return;
									openNewEvent(date);
								}}
								onDayContextMenu={onDayContextMenu}
								onSchemeChipClick={(date) => setDayConfig(date)}
								onItemMouseDown={handleItemMouseDown}
								onItemContextMenu={onItemContextMenu}
								onWorkOrderClick={(wo) => setModal({ mode: "wo", wo })}
								menuTargetKey={menuTargetKey}
							/>
						)}

						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								flexWrap: "wrap",
								gap: 1,
								mt: 1,
							}}
						>
							<CalendarLegend />

							<UndoRedoButtons
								undoTitle={undoTitle}
								redoTitle={redoTitle}
								canUndo={canUndo}
								canRedo={canRedo}
								busy={undoBusy}
								undoLoading={undoMutation.isLoading}
								redoLoading={redoMutation.isLoading}
								onUndo={() => undoMutation.mutate()}
								onRedo={() => redoMutation.mutate()}
							/>
						</Box>
					</Paper>
				</Grid2>
			</Grid2>

			{menus}
			{deleter.dialogs}

			{dayConfig && assetId != null && (
				<DayConfigModal
					assetId={assetId}
					date={dayConfig}
					currentKind={dayKindOf(shiftsByDay.get(toDateKey(dayConfig)))}
					lineDefaultKind={lineDefaultKind}
					saving={dayConfigMutation.isLoading}
					onSave={(kind, spells) => dayConfigMutation.mutate({ kind, spells })}
					onClose={() => setDayConfig(null)}
				/>
			)}

			{editorProps && (
				<EventEditorModal
					open
					onHide={(success, refs, workOrderNumber) => {
						setModal(null);
						if (success) {
							if (refs) recordChanges(refs);
							if (workOrderNumber)
								setNotificationMessage(
									`Work order ${workOrderNumber} saved successfully`,
								);
						}
					}}
					assetId={assetId}
					areaIds={areaIds}
					lineEvents={calData?.events ?? []}
					{...editorProps}
				/>
			)}

			{moveRequest?.item.event.EventTypeCode === "B" ? (
				<BreakPlanConfirmationModal
					request={{
						eventId: moveRequest.item.event.EventId,
						assetIds:
							moveRequest.item.event.AssetId != null
								? [moveRequest.item.event.AssetId]
								: [],
						start: moveRequest.newStart.toISOString(),
						end: moveRequest.newEnd.toISOString(),
						comments: moveRequest.item.event.Comments,
					}}
					onSaved={handleMoveSaved}
					onCancel={() => setMoveRequest(null)}
				/>
			) : (
				moveRequest && (
					<ConfirmMoveModal
						loading={moveMutation.isLoading}
						moveData={moveRequest}
						onConfirm={() => moveRequest && moveMutation.mutate(moveRequest)}
						onCancel={() => setMoveRequest(null)}
					/>
				)
			)}

			<Backdrop open={!!selectedWo && !editWorkOrder && isWorkOrderLoading}>
				<CircularProgress color="inherit" />
			</Backdrop>

			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
